import * as React from 'react';
import { PATHS } from '@ids/router/routes';
import { useFetchAPI, useIDSPush } from '@ids/hooks';
import { useCookies } from '@ids/context/Cookies';
import { useAppShellPush } from '@ids/appshell';

export const REDIRECT_FLAG_KEY = 'ww_redirect_to_overstep';
export const IDS_PRODUCT_SKU_KEY = 'ww_ids_product_sku';

// Unit testing this is not easy, so we're going to just do e2e testing
// istanbul ignore next
export default function useOverstepPage() {
    const idsPush = useIDSPush();
    const push = useAppShellPush();
    const { makeRequest } = useFetchAPI();
    const [cookies, setCookie, removeCookie] = useCookies([REDIRECT_FLAG_KEY, IDS_PRODUCT_SKU_KEY], {
        path: '/',
        maxAge: 60 * 60 * 24 * 7,
    });

    const enableRedirectsToOverstep = React.useCallback(
        (idsProductSku: string) => {
            if (!idsProductSku) {
                throw new Error('idsProductSku is required');
            }
            setCookie(REDIRECT_FLAG_KEY, 'true');
            setCookie(IDS_PRODUCT_SKU_KEY, idsProductSku);
        },
        [setCookie]
    );

    const disableRedirectsToOverstep = React.useCallback(async () => {
        await removeCookie(REDIRECT_FLAG_KEY);
        await removeCookie(IDS_PRODUCT_SKU_KEY);
    }, [removeCookie]);

    const hasRedirectToOverstepCookie = React.useCallback(() => {
        return cookies?.get(REDIRECT_FLAG_KEY) === 'true';
    }, [cookies]);

    const checkIfRedirectsToOverstep = async (pathname: string) => {
        const idsSku = cookies?.get(IDS_PRODUCT_SKU_KEY);

        const cartResponse = await makeRequest<{ sku: string; quantity: number }[]>('cart');
        const isIdsProductInCart = cartResponse?.data?.some(prod => prod.sku === idsSku);

        if (cartResponse.success && !isIdsProductInCart) {
            await disableRedirectsToOverstep();
            push(pathname);
        }

        if (isIdsProductInCart) {
            idsPush(PATHS.OVERSTEP);
        }
    };

    return {
        enableRedirectsToOverstep,
        disableRedirectsToOverstep,
        checkIfRedirectsToOverstep,
        hasRedirectToOverstepCookie,
    };
}
